import React from 'react';
import { useToast } from '../hooks/useToast';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Você pode logar o erro em um serviço externo aqui, se quiser
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div>Ocorreu um erro inesperado. Tente recarregar a página.</div>;
    }
    return this.props.children;
  }
}

// Componente funcional para usar o toast
export function ErrorBoundaryWithToast({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const [hasError, setHasError] = React.useState(false);

  // Fallback para o ErrorBoundary
  const fallback = React.useMemo(() => (
    <div>Ocorreu um erro inesperado. Tente recarregar a página.</div>
  ), []);

  // Wrapper para capturar o erro e disparar o toast
  class Boundary extends ErrorBoundary {
    render() {
      if (this.state.hasError && !hasError) {
        setHasError(true);
      }
      return super.render();
    }
  }

  React.useEffect(() => {
    if (hasError) {
      showToast('Ocorreu um erro inesperado. Tente novamente.', 'error');
    }
  }, [hasError, showToast]);

  return (
    <Boundary fallback={fallback}>
      {children}
    </Boundary>
  );
} 